import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import GoldenSetPicker, { describeGoldenSet } from "./GoldenSetPicker";

export const STEPS = ["Choose a golden set", "Choose engine versions", "Name the run", "Review and start"];

// Production is the engine version live today; test is the one being tried.
const EMPTY = { goldenSet: null, production: "cws", test: "convoengine_plus", tag: "" };

// Why "Next" is disabled on a step, or null when the step is complete.
const missing = (step, form) => {
  if (step === 0 && !form.goldenSet) return "Choose a golden set";
  if (step === 1 && (!form.production || !form.test)) return "Choose a production and a test engine version";
  if (step === 1 && form.production === form.test) return "The production and test engine versions must be different";
  if (step === 2 && !form.tag.trim()) return "A run name is required";
  return null;
};

const ReviewRow = ({ label, children }) => (
  <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 2 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
      {label}
    </Typography>
    <Typography variant="body2">{children}</Typography>
  </Stack>
);

// The guided setup on /evaluations. The page owns every API call; this panel
// collects a golden set, the production and test engine versions and a run
// name, then calls onStart.
const RunSetupPanel = ({
  presetOptions,
  goldenSets,
  goldenSetsLoading,
  onSearchGoldenSets,
  onAddGoldenSet,
  existingTags,
  onStart,
}) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [starting, setStarting] = useState(false);

  const reason = missing(step, form);
  const nameTaken = form.tag.trim() && existingTags.includes(form.tag.trim());
  const labelFor = (value) => presetOptions.find((p) => p.value === value)?.label || value;

  const close = () => {
    setOpen(false);
    setStep(0);
    setForm(EMPTY);
  };

  const versionSelect = (role, label, helper) => (
    <FormControl fullWidth>
      <InputLabel id={`${role}-engine-label`}>{label}</InputLabel>
      <Select
        labelId={`${role}-engine-label`}
        label={label}
        value={form[role]}
        onChange={(e) => setForm((f) => ({ ...f, [role]: e.target.value }))}
        renderValue={labelFor}
      >
        {presetOptions.map((p) => (
          <MenuItem key={p.value} value={p.value}>
            <ListItemText primary={p.label} secondary={p.description} />
          </MenuItem>
        ))}
      </Select>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        {helper}
      </Typography>
    </FormControl>
  );

  const handleStart = async () => {
    setStarting(true);
    try {
      const started = await onStart({
        tag: form.tag.trim(),
        production: form.production,
        test: form.test,
        goldenSet: form.goldenSet,
      });
      if (started) close();
    } finally {
      setStarting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
            <Stack spacing={0.5}>
              <Typography variant="h6" component="h2">
                Compare engine versions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                A comparison run sends every question in a golden set to each engine version you choose. The run page
                then shows where their answers differ, so you can choose which engine version to release.
              </Typography>
            </Stack>
            {!open && (
              <Box sx={{ flexShrink: 0 }}>
                <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={() => setOpen(true)}>
                  Start a comparison run
                </Button>
              </Box>
            )}
          </Stack>

          {open && (
            <>
              <Stepper activeStep={step} alternativeLabel>
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ pt: 1 }}>
                {step === 0 && (
                  <GoldenSetPicker
                    goldenSets={goldenSets}
                    loading={goldenSetsLoading}
                    value={form.goldenSet}
                    onChange={(goldenSet) => setForm((f) => ({ ...f, goldenSet }))}
                    onSearch={onSearchGoldenSets}
                    onAdd={onAddGoldenSet}
                  />
                )}

                {step === 1 && (
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    {versionSelect("production", "Production engine version", "The engine version live today")}
                    {versionSelect("test", "Test engine version", "The engine version you want to try")}
                  </Stack>
                )}

                {step === 2 && (
                  <TextField
                    label="Run name"
                    placeholder="e.g. v1.2.0 or after-script-update"
                    value={form.tag}
                    onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))}
                    fullWidth
                    helperText={
                      nameTaken
                        ? "A run with this name already exists. The results will be added to it."
                        : "A short name to find and compare this run later"
                    }
                    color={nameTaken ? "warning" : undefined}
                    focused={nameTaken || undefined}
                  />
                )}

                {step === 3 && (
                  <Stack spacing={1}>
                    <ReviewRow label="Golden set">{form.goldenSet.name}</ReviewRow>
                    <ReviewRow label="Questions and scripts">{describeGoldenSet(form.goldenSet)}</ReviewRow>
                    <ReviewRow label="Dialogue order">
                      {form.goldenSet.has_line_order
                        ? "Yes. Results will be in dialogue order."
                        : "No dialogue order. Results will be sorted by script and question."}
                    </ReviewRow>
                    <ReviewRow label="Production engine version">{labelFor(form.production)}</ReviewRow>
                    <ReviewRow label="Test engine version">{labelFor(form.test)}</ReviewRow>
                    <ReviewRow label="Run name">{form.tag.trim()}</ReviewRow>
                    {nameTaken && (
                      <Alert severity="warning">
                        A run with this name already exists. The results will be added to it.
                      </Alert>
                    )}
                  </Stack>
                )}
              </Box>

              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                {reason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mr: "auto" }}>
                    {reason}
                  </Typography>
                )}
                <Button onClick={close} disabled={starting}>
                  Cancel
                </Button>
                <Button onClick={() => setStep((s) => s - 1)} disabled={step === 0 || starting}>
                  Back
                </Button>
                {step < STEPS.length - 1 ? (
                  <Button variant="contained" onClick={() => setStep((s) => s + 1)} disabled={!!reason}>
                    Next
                  </Button>
                ) : (
                  <Button variant="contained" onClick={handleStart} disabled={starting}>
                    {starting ? <CircularProgress size={18} /> : "Start run"}
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default RunSetupPanel;
